import { useLazyQuery } from '@apollo/client/react';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CoachMemberListDocument } from '../graphql/types';

const SEARCH_DEBOUNCE_MS = 500;

interface Options {
  /**
   * Hold the query back until the surface using it is actually open. Every
   * caller is a modal or flyout, and none of them should fetch while closed.
   */
  skip?: boolean;
  /** Restrict to members who have signed in at least once. */
  hasSignedIn?: boolean;
  pageSize?: number;
}

/**
 * The coach's members across all of their clients, filtered by name as they
 * type. `coachMemberList` returns one `users` connection per client, so the
 * pages are flattened into a single list.
 *
 * Options are primitives on purpose: they feed the query variables, and an
 * object would change identity every render, refetch, re-render and loop.
 */
export const useMemberSearch = ({
  skip = false,
  hasSignedIn,
  pageSize = 10,
}: Options = {}) => {
  const [searchValue, setSearchValue] = useState('');
  const [fetchMembers, { data, loading, error }] = useLazyQuery(
    CoachMemberListDocument,
  );

  useEffect(() => {
    if (skip) {
      return;
    }

    fetchMembers({
      variables: {
        input: { first: pageSize },
        membersFilter: {
          ...(hasSignedIn === undefined ? {} : { hasSignedIn }),
          ...(searchValue ? { name: searchValue } : {}),
        },
      },
    });
  }, [fetchMembers, hasSignedIn, pageSize, searchValue, skip]);

  /**
   * The coach's clients, each with its own page of members. Exposed because
   * `BookSessionModal` narrows to the selected coach's clients before
   * flattening; most callers want `members`.
   */
  const clients = useMemo(
    () => (data?.self?.coachData?.clients.edges ?? []).map(({ node }) => node),
    [data],
  );

  const members = useMemo(
    () =>
      flatten(
        clients.map((client) => client.users.edges.map((edge) => edge.node)),
      ),
    [clients],
  );

  /** For typing: waits for the coach to stop before refetching. */
  const search = useMemo(
    () => debounce(setSearchValue, SEARCH_DEBOUNCE_MS),
    [],
  );

  // The trailing call would otherwise land after the surface has closed.
  useEffect(() => () => search.cancel(), [search]);

  /** For seeding the term in code, where there is nothing to wait for. */
  const setSearch = useCallback(
    (value: string) => {
      search.cancel();
      setSearchValue(value);
    },
    [search],
  );

  const reset = useCallback(() => setSearch(''), [setSearch]);

  return {
    clients,
    members,
    loading,
    error,
    search,
    setSearch,
    searchValue,
    reset,
  };
};
